import { type IPP_SUPPORTED_LOCALE, IPP_SUPPORTED_LOCALES, IPP_DEFAULT_LOCALE } from './intl.types'

export const LOCALE_STORAGE_KEY = 'userLocale'
/* Intentionally not using 'locale' as a key because it is currently used
 * by specific tools. We can later update it if we decide to sync the individual
 * tool locales to IPP's locale.
 */
export const URL_PARAM_KEY = 'ipp_locale'

/**
 * Check if a locale is valid
 * @param locale - The locale to check
 * @returns True if the locale is valid, false otherwise
 */

export const isValidLocale = (locale: string): boolean =>
  IPP_SUPPORTED_LOCALES.includes(locale as IPP_SUPPORTED_LOCALE)

export const isCypressWindow = () =>
  typeof window !== 'undefined' && navigator?.userAgent?.includes('Cypress')

const getUrlLocale = (): IPP_SUPPORTED_LOCALE | null => {
  try {
    const currentUrl = new URL(window.location.href)
    const urlLocale = currentUrl?.searchParams?.get(URL_PARAM_KEY)
    if (urlLocale && isValidLocale(urlLocale)) {
      // Try to save to localStorage (don't fail if this doesn't work)
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, urlLocale)
      } catch (storageError) {
        // Continue even if localStorage fails
      }

      // Try to remove the URL parameter (don't fail if this doesn't work)
      try {
        currentUrl.searchParams.delete(URL_PARAM_KEY)
        window.history.replaceState({}, '', currentUrl.toString())
      } catch (historyError) {
        // Continue even if URL cleaning fails
      }

      return urlLocale as IPP_SUPPORTED_LOCALE
    }
  } catch (error) {
    // If URL parsing fails (e.g., in tests or invalid URLs), continue to localStorage fallback
    // This is expected in test environments where window.location.href might not be properly set
  }

  return null
}

/**
 * Get the user's locale from URL parameters, then fall back to localStorage, then
 * fall back to the browser's locale if the user's locale is missing or not supported.
 * If the browser locale is missing or not supported, fall back to the default en-US locale.
 *
 * @param defaultLocale - The default locale to use if the user's locale is not supported
 * @returns The user's locale or the default en-US locale if the user's locale is not supported
 */
export const getStoredLocaleWithFallback = (
  defaultLocale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE
) => {
  // First, check URL parameters
  const urlLocale = getUrlLocale()
  if (urlLocale) {
    return urlLocale
  }

  // Second, check localStorage
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && isValidLocale(stored)) {
      return stored as IPP_SUPPORTED_LOCALE
    }
  } catch (error) {
    console.warn('Failed to access localStorage for user locale:', error)
  }

  // Third, check browser locale
  const browserLocale = navigator.language
  if (browserLocale && isValidLocale(browserLocale)) {
    return browserLocale as IPP_SUPPORTED_LOCALE
  }

  // Finally, return default locale
  return defaultLocale
}
